import { createSelectorFunctions } from 'auto-zustand-selectors-hook'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { FormManager, StateValue } from '@/panels/signup/FormStateMachine'

type SignupState = {
  formState: StateValue
  email: string
  verificationCode: string
  phoneNumber: string
  employmentStatus: string | undefined
  occupation: string | undefined
  occupationDetails: string
  purpose: string | undefined
  additionalInfo: {
    sourceOfFunds: string
    intendedUseOfUSDC: string
    walletProvider: string
    walletAddress: string
    howDidYouHear: string
    whoReferredYou: string
    previousCryptoExperience: string
  }
  verifyId: string
  sessionId: string
  referralCode: string
  oliuVerifyType: string
  oliuPhoneVerified: boolean
}

const initialState: SignupState = {
  formState: FormManager.getCurrentState(),
  email: '',
  verificationCode: '',
  phoneNumber: '',
  employmentStatus: undefined,
  occupation: undefined,
  occupationDetails: '',
  purpose: localStorage.getItem('purpose') || undefined,
  additionalInfo: {
    sourceOfFunds: '',
    intendedUseOfUSDC: '',
    walletProvider: '',
    walletAddress: '',
    howDidYouHear: '',
    whoReferredYou: '',
    previousCryptoExperience: '',
  },
  verifyId: '',
  sessionId: '',
  referralCode: '',
  oliuVerifyType: '',
  oliuPhoneVerified: sessionStorage.getItem('oliuPhoneVerified') === 'true',
}

type Actions = {
  setEmail: (email: string) => void
  setPhoneNumber: (email: string) => void
  setOccupation: (occupation: string | undefined) => void
  setOccupationDetails: (occupationDetails: string) => void
  setEmploymentStatus: (employmentStatus: string) => void
  setPurpose: (purpose: string) => void
  setAdditionalInfo: (additionalInfo: SignupState['additionalInfo']) => void
  setSourceOfFunds: (sourceOfFunds: string) => void
  setIntendedUseOfUSDC: (intendedUseOfUSDC: string) => void
  setWalletProvider: (walletProvider: string) => void
  setWalletAddress: (walletAddress: string) => void
  setHowDidYouHear: (howDidYouHear: string) => void
  setWhoReferredYou: (whoReferredYou: string) => void
  setPreviousCryptoExperience: (previousCryptoExperience: string) => void
  setVerificationCode: (code: string) => void
  setOliuVerifyId: (verifyId: string) => void
  setSessionId: (sessionId: string) => void
  setReferralCode: (referralCode: string) => void
  setOliuVerifyType: (oliuVerifyType: string) => void
  setOliuPhoneVerified: (oliuPhoneVerified: boolean) => void
  nextState: () => void
  prevState: () => void
  jumpTo: (page: StateValue) => void
  complete: () => void
  reset: () => void
}

const createSignupStore = create<SignupState & Actions>()(
  immer<SignupState & Actions>((set) => ({
    ...initialState,
    setEmail: (email: string) =>
      set((state) => {
        state.email = email
      }),
    setPhoneNumber: (phoneNumber: string) =>
      set((state) => {
        state.phoneNumber = phoneNumber
      }),
    setOliuVerifyId: (verifyId: string) =>
      set((state) => {
        state.verifyId = verifyId
      }),
    setSessionId: (sessionId: string) =>
      set((state) => {
        state.sessionId = sessionId
      }),
    setReferralCode: (referralCode: string) =>
      set((state) => {
        state.referralCode = referralCode
      }),
    nextState: () =>
      set((state) => {
        FormManager.next()
        state.formState = FormManager.getCurrentState()
      }),
    prevState: () =>
      set((state) => {
        FormManager.back()
        state.formState = FormManager.getCurrentState()
      }),
    complete: () =>
      set((state) => {
        FormManager.complete()
        state.formState = FormManager.getCurrentState()
      }),
    jumpTo: (page: StateValue) =>
      set((state) => {
        FormManager.jumpTo(page)
        state.formState = FormManager.getCurrentState()
      }),
    setOccupation: (occupation: string | undefined) => {
      set((state) => {
        if (state.occupation !== null) {
          state.occupation = occupation
        }
      })
    },
    setOccupationDetails: (occupationDetails: string) => {
      set((state) => {
        if (state.occupationDetails !== null) {
          state.occupationDetails = occupationDetails
        }
      })
    },
    setEmploymentStatus: (employmentStatus: string) => {
      set((state) => {
        if (state.employmentStatus !== null) {
          state.employmentStatus = employmentStatus
        }
      })
    },
    setPurpose: (purpose: string) => {
      set((state) => {
        if (state.purpose !== null) {
          state.purpose = purpose
        }
      })
    },
    setVerificationCode: (code: string) =>
      set((state) => {
        state.verificationCode = code
      }),
    setOliuVerifyType: (oliuVerifyType: string) =>
      set((state) => {
        state.oliuVerifyType = oliuVerifyType
      }),
    setOliuPhoneVerified: (oliuPhoneVerified: boolean) =>
      set((state) => {
        state.oliuPhoneVerified = oliuPhoneVerified
      }),
    setAdditionalInfo: (additionalInfo: SignupState['additionalInfo']) =>
      set((state) => {
        state.additionalInfo = additionalInfo
      }),
    setSourceOfFunds: (sourceOfFunds: string) =>
      set((state) => {
        state.additionalInfo.sourceOfFunds = sourceOfFunds
      }),
    setIntendedUseOfUSDC: (intendedUseOfUSDC: string) =>
      set((state) => {
        state.additionalInfo.intendedUseOfUSDC = intendedUseOfUSDC
      }),
    setWalletProvider: (walletProvider: string) =>
      set((state) => {
        state.additionalInfo.walletProvider = walletProvider
      }),
    setWalletAddress: (walletAddress: string) =>
      set((state) => {
        state.additionalInfo.walletAddress = walletAddress
      }),
    setHowDidYouHear: (howDidYouHear: string) =>
      set((state) => {
        state.additionalInfo.howDidYouHear = howDidYouHear
      }),
    setWhoReferredYou: (whoReferredYou: string) =>
      set((state) => {
        state.additionalInfo.whoReferredYou = whoReferredYou
      }),
    setPreviousCryptoExperience: (previousCryptoExperience: string) =>
      set((state) => {
        state.additionalInfo.previousCryptoExperience = previousCryptoExperience
      }),
    reset: () => {
      set(() => {
        FormManager.reset()
        return initialState
      })
    },
  }))
)

export const useSignupStore = createSelectorFunctions(createSignupStore)
